import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Slider from "react-slick";
import ModalContent from "./ModalContent";
import ModalToggler from "./ModalToggler";

const UpcomingEvents = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const normalizeBaseUrl = (baseUrl) => {
    if (!baseUrl) return "";
    return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  };

  const normalizePathPrefix = (prefix) => {
    if (!prefix) return "";
    if (!prefix.startsWith("/")) return `/${prefix}`;
    return prefix;
  };

  const apiBaseUrlCandidates = useMemo(() => {
    const base = normalizeBaseUrl(process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:2902");
    const version = normalizePathPrefix(process.env.REACT_APP_API_VERSION || "");
    const primary = `${base}${version}`;

    if (version) return [primary];
    return [primary, `${base}/api/v1`, `${base}/api`];
  }, []);

  const toYmdFromIso = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Helper function to format the date without the weekday
  const formatDate = (dateString) => {
    if (!dateString) return "Recurring"; // Display "Recurring" for permanent events
    const localDate = new Date(dateString + "T00:00:00"); // Adjust to local time
    const options = { year: "numeric", month: "long", day: "numeric" }; // Only show month, day, year
    return localDate.toLocaleDateString("en-US", options); // Adjust locale as needed
  };

  const mapBackendEventToUiEvent = (event) => {
    const id = event?._id || event?.id;
    const date = event?.type === "recurring" ? "" : toYmdFromIso(event?.start_date);
    return {
      id,
      title: event?.title || "",
      image: event?.image?.url || "",
      date,
      startTime: event?.from_time || "",
      endTime: event?.to_time || "",
    };
  };

  const fetchUpcomingEvents = async () => {
    setLoading(true);
    setError("");

    try {
      let lastError = null;
      let allEvents = [];

      for (const candidate of apiBaseUrlCandidates) {
        try {
          console.log(`UpcomingEvents: Trying API candidate: ${candidate}`);
          
          // Fetch all events without status filter, handling pagination
          let page = 1;
          let totalPages = 1;
          
          do {
            console.log(`UpcomingEvents: Fetching page ${page} of ${totalPages}...`);
            const allRes = await axios.get(`${candidate}/event/live`, { 
              params: { page: page }
            });
            
            console.log(`UpcomingEvents: Page ${page} API response:`, {
              success: allRes.data.success,
              result: allRes.data.result,
              page: allRes.data.page,
              totalPages: allRes.data.totalPages,
              total: allRes.data.total
            });
            
            const pageEvents = allRes?.data?.result || [];
            console.log(`UpcomingEvents: Page ${page} returned ${pageEvents.length} events`);
            
            allEvents = [...allEvents, ...pageEvents];
            
            totalPages = allRes?.data?.totalPages || 1;
            console.log(`UpcomingEvents: Total pages available: ${totalPages}, fetched pages: ${page}`);
            
            page++;
            
            // Safety check to prevent infinite loops
            if (page > 10) {
              console.log("UpcomingEvents: Safety check - stopping at page 10");
              break;
            }
          } while (page <= totalPages);
          
          console.log("UpcomingEvents: Total events fetched:", allEvents.length);
          
          // Remove duplicate events by ID
          const uniqueEvents = allEvents.filter((event, index, self) => 
            index === self.findIndex((e) => e._id === event._id)
          );
          
          console.log("UpcomingEvents: Unique events after deduplication:", uniqueEvents.length);
          
          // Filter events to include upcoming and recurring events
          const currentDate = new Date();
          const upcomingFiltered = uniqueEvents.filter(event => {
            // Include recurring events (no start_date) and future events
            if (!event.start_date) return true;
            return new Date(event.start_date) >= currentDate;
          });
          
          console.log("UpcomingEvents: Upcoming events after filtering:", upcomingFiltered.length);
          
          setUpcomingEvents(upcomingFiltered.map(mapBackendEventToUiEvent));
          lastError = null;
          break;
        } catch (innerErr) {
          console.log("UpcomingEvents: API candidate failed:", innerErr);
          lastError = innerErr;
        }
      }

      if (lastError) throw lastError;
    } catch (e) {
      console.error("UpcomingEvents: Final error:", e);
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load events from backend. Check backend URL/API version and that the server is running.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  // Settings for the Slick slider
  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3, // Always try to show 3 items
    slidesToScroll: 1,
    initialSlide: 0,
    autoplay: true,
    autoplaySpeed: 7000,
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          initialSlide: 0,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 0,
        },
      },
    ],
  };

  return (
    <section className="container-fluid">
      <div className="pb-4 text-center px-4 px-md-0">
        <div className="fs-ss-40 fw-600">Upcoming Events</div>
        <div className="text-ss-secondary fs-ss-22 fw-500">
          Join Us in Building a Stronger Community
        </div>
      </div>
      <section className="container-lg">
        <div className="row justify-content-center px-md-5">
          {loading ? <p className="text-center fs-ss-18 fw-500">Loading...</p> : null}
          {error ? <p className="text-center fs-ss-18 fw-500">{error}</p> : null}

          {/* Modal Content for Upcoming Events */}
          {!loading && !error && upcomingEvents.length > 0 ? (
            upcomingEvents.map((event) => (
              <ModalContent
                key={event.id}
                Img={event.image}
                Id={event.id}
                Title={event.title}
                Date={formatDate(event.date)}
                StartTime={`Start Time: ${event.startTime}`}
                EndTime={event.endTime ? `End Time: ${event.endTime}` : ""}
              />
            ))
          ) : !loading && !error ? (
            <p className="text-center fs-ss-18 fw-500">No upcoming events.</p>
          ) : null}

          {/* Conditionally render the Slider */}
          {!loading && !error && upcomingEvents.length >= 3 ? (
            <Slider {...settings}>
              {upcomingEvents.map((event) => (
                <div key={event.id} className="slider-item">
                  <ModalToggler
                    w90="w-90"
                    Img={event.image}
                    Id={event.id}
                    Title={event.title}
                    Date={formatDate(event.date)}
                    StartTime={`Start Time: ${event.startTime}`}
                    EndTime={event.endTime ? `End Time: ${event.endTime}` : ""}
                  />
                </div>
              ))}
            </Slider>
          ) : !loading && !error ? (
            // If fewer than 3 events, render them in a single row with col-4
            upcomingEvents.map((event) => (
              <div key={event.id} className="col-md-6 col-lg-4 mb-3">
                <ModalToggler
                  w90="w-90"
                  Img={event.image}
                  Id={event.id}
                  Title={event.title}
                  Date={formatDate(event.date)}
                  StartTime={`Start Time: ${event.startTime}`}
                  EndTime={event.endTime ? `End Time: ${event.endTime}` : ""}
                />
              </div>
            ))
          ) : null}
        </div>
      </section>
    </section>
  );
};

export default UpcomingEvents;
